import { useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { CopyableCell } from '../../components/ui/CopyableCell';
import { SelectField } from '../../components/ui/SelectField';
import { Textarea } from '../../components/ui/textarea';
import { Spinner } from '../../components/ui/Spinner';
import { formatExactTimestamp } from '../../lib/inboxView';
import { hitUnrecognisedCeiling, unrecognisedCeilingMessage } from '../../lib/leadRelay/retry';
import { describeEmptyAssignChoices, describeRouting, describeUnassigned, isPayloadProblem } from '../../lib/leadRelay/assignment';
import { platformLabel } from '../../lib/leadRelay/platform';
import { describeRouteSuggestion } from '../../lib/leadRelay/unroutable';
import {
  assignConfirmation,
  canAssign,
  mayMarkKeyedManually,
  canEditLead,
  canResend,
  EDITABLE_FIELD_LABELS,
  editableFieldsFor,
  editNotes,
  failureLabel,
  hasUnsentEdit,
  INTEREST_FALLBACK_LABELS,
  isInFlight,
  KEYED_MANUALLY_NOTE_MAX,
  keyedManuallyConfirmation,
  keyedManuallyNoteRequired,
  rekeyFields,
  requiredGapText,
  resendConfirmation,
  STATUS_LABELS,
  testModeInFlightNote,
  nameNotes,
  payloadNotes,
} from '../../lib/leadRelay/logView';
import { systemName } from '../../lib/leadRelay/providerVocabulary';
import type { EditableLeadField, LeadAssignmentView, LeadProvider, LeadSubmissionDetail, LeadSubmissionStatus } from '../../types/leadRelay';
import type { RelayOutcome } from './api';

// One lead, for a PERSON.
//
// ⚠️⚠️ This is the screen someone works from when a provider has refused us and
// leads have to be keyed in by hand (docs/lead-relay.md §6) — the fallback the
// whole design rests on. So the customer's details come FIRST, as labelled
// values in the order the dealer's form asks for them, each with a copy button.
// Never a JSON dump: a person re-keying a lead must not have to read one.
// The delivery attempts follow, below, for whoever is diagnosing.
//
// ⚠️ PROVIDER VOCABULARY (step 3): the destination's label, who requires a
// field and the system named in every sentence come from the lead's provider
// (providerVocabulary.ts). An unassigned lead has none yet and is named
// neutrally — never as VirtualYard by default.

export function statusBadgeVariant(
  status: LeadSubmissionStatus,
): 'success' | 'warning' | 'destructive' | 'secondary' | 'outline' {
  switch (status) {
    case 'submitted':
      return 'success';
    case 'failed':
      return 'destructive';
    case 'keyed_manually':
      return 'secondary';
    case 'retrying':
    case 'received':
      return 'warning';
    case 'test':
      return 'secondary';
    case 'unassigned':
      return 'warning';
    default:
      return 'outline';
  }
}

/**
 * The result of Assign and send, shown inline. ⚠️ The same "accepted is not
 * received" treatment as every other send: the provider accepting the POST says
 * nothing about the enquiry reaching the right desk.
 */
export function AssignSendResult({
  routeName,
  outcome,
  error,
  provider,
}: {
  routeName: string;
  outcome: RelayOutcome | null;
  error: string | null;
  /** The lead's provider, when known (the refetched lead carries it). */
  provider: LeadProvider | null;
}) {
  const system = provider ? systemName(provider) : null;
  if (!outcome) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="assign-send-result">
        <p className="font-medium">Not assigned to {routeName}: {error ?? 'the request failed'}.</p>
        <p className="mt-1">Check the lead's status above before trying again — it may have been assigned meanwhile.</p>
      </div>
    );
  }
  const ok = outcome.status === 'submitted';
  return (
    <div
      className={`rounded-md border p-3 text-sm ${ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-red-200 bg-red-50 text-red-800'}`}
      data-testid="assign-send-result"
    >
      <p className="font-medium">
        {ok
          ? `Assigned to ${routeName} and sent — ${system ? `${system} accepted it` : 'it was accepted'}.`
          : `Assigned to ${routeName}, but not delivered yet — ${STATUS_LABELS[outcome.status].toLowerCase()}${outcome.failureReason ? `: ${failureLabel(outcome.failureReason, provider)}` : ''}.`}
      </p>
      {ok ? (
        <p className="mt-1">
          Accepted is not the same as received: confirm with {routeName} that the enquiry arrived.
        </p>
      ) : (
        <p className="mt-1">It is now an ordinary lead on that route: retries, Resend and hand-keying apply as for any other.</p>
      )}
      {outcome.lastAttempt?.error && <p className="mt-1">{outcome.lastAttempt.error}</p>}
    </div>
  );
}

/**
 * Everything a person needs to assign a lead by hand. ⚠️ The form-name hint is
 * boxed and labelled as a SUGGESTION, and the panel says outright that the lead
 * has been sent nowhere — it must be impossible to read the hint as a decision.
 */
function UnassignedSection({
  view,
  onAssign,
  assignPending,
  relayConfigured,
  confirmLines,
}: {
  view: LeadAssignmentView;
  /** Absent for a non-admin: assigning is admin-only (the server refuses it too). */
  onAssign?: (routeId: string, routeName: string, provider: LeadProvider | null) => void;
  assignPending: boolean;
  relayConfigured: boolean;
  confirmLines: (routeName: string) => string[];
}) {
  const [routeId, setRouteId] = useState('');
  const [confirming, setConfirming] = useState(false);
  // ⚠️⚠️ THE PROVIDER PICKER: only when the lead's provider CANNOT be known
  // (view.providerAssertion is null otherwise — a known provider is never
  // overridable). Until a person asserts one, no route is offered at all.
  const [asserted, setAsserted] = useState<LeadProvider | ''>('');
  const assertion = view.providerAssertion?.find((a) => a.provider === asserted) ?? null;
  const choices = view.providerAssertion ? (assertion?.choices ?? []) : view.choices;
  const emptyReason = view.providerAssertion ? (assertion?.emptyReason ?? null) : view.choicesEmptyReason;
  const effectiveProvider = view.providerAssertion ? (asserted || null) : view.leadProvider;
  const chosen = choices.find((c) => c.id === routeId) ?? null;
  const noLead = isPayloadProblem(view.reason);
  const reason = describeUnassigned(view.reason, {
    accountId: view.accountId,
    ...(view.ambiguousKeys ? { ambiguousKeys: view.ambiguousKeys } : {}),
    accountName: view.accountName,
    activeRouteNames: view.routesOnAccount.filter((r) => r.active).map((r) => r.name),
    inactiveRouteNames: view.routesOnAccount.filter((r) => !r.active).map((r) => r.name),
    accountChecked: true,
  });
  return (
    <section className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="unassigned-section">
      <p className="font-medium">Unassigned — this lead has NOT been sent to any dealer.</p>
      <p data-testid="unassigned-reason">{reason}</p>
      <dl className="grid gap-x-3 gap-y-1 sm:grid-cols-[10rem_1fr]">
        <dt className="text-amber-800">account_id sent</dt>
        <dd>{view.accountId ?? 'none'}</dd>
        <dt className="text-amber-800">Matched account</dt>
        <dd>{view.accountId === null ? '—' : (view.accountName ?? 'none — no Kyck account has this id')}</dd>
        <dt className="text-amber-800">Routes on that account</dt>
        <dd>
          {view.routesOnAccount.length === 0
            ? 'none'
            : view.routesOnAccount
                .map((r) => `${r.name}${r.active ? '' : ' (inactive)'}${r.testMode ? ' (test mode)' : ''}`)
                .join(', ')}
        </dd>
      </dl>
      {noLead ? (
        // ⚠️ No usable Rawdata: there is no lead to send, so no route to pick.
        <p data-testid="no-lead-to-send">
          There is nothing to send. Read the raw body below, key the lead in by hand, then mark it keyed.
        </p>
      ) : (
        <p className="rounded border border-dashed border-amber-400 bg-white/60 p-2" data-testid="route-suggestion">
          {describeRouteSuggestion(view.suggestion)} The form name does not route leads — this is a hint, not something
          the system did.
        </p>
      )}

      {noLead ? null : !onAssign ? (
        <p data-testid="assign-admin-only">Only an admin can assign and send an unassigned lead, or mark it keyed in by hand.</p>
      ) : view.providerAssertion && !confirming && asserted === '' ? (
        <div className="space-y-2" data-testid="provider-picker">
          <p>
            The relay cannot tell which system this lead belongs to. If you know — from the form, the campaign or the
            details below — say so, and its routes are offered. Your choice is recorded on the lead.
          </p>
          <div className="w-72">
            <SelectField
              value={asserted}
              onValueChange={(v) => {
                setAsserted(v as LeadProvider | '');
                setRouteId('');
              }}
              options={[
                { value: '', label: 'This lead is for…' },
                ...view.providerAssertion.map((a) => ({ value: a.provider, label: systemName(a.provider) })),
              ]}
              aria-label="Which system this lead is for"
            />
          </div>
        </div>
      ) : choices.length === 0 ? (
        <div>
          <p data-testid="assign-no-choices">
            {emptyReason ? describeEmptyAssignChoices(emptyReason, effectiveProvider) : 'No route can take it right now.'}
          </p>
          {view.providerAssertion && (
            <Button size="sm" variant="outline" className="mt-2" onClick={() => setAsserted('')}>
              Choose a different system
            </Button>
          )}
        </div>
      ) : !confirming ? (
        <div className="flex flex-wrap items-end gap-2">
          {view.providerAssertion && asserted !== '' && (
            <p className="w-full" data-testid="provider-asserted">
              You said this lead is for {systemName(asserted)}.{' '}
              <button type="button" className="underline" onClick={() => setAsserted('')}>
                Change
              </button>
            </p>
          )}
          <div className="w-72">
            <SelectField
              value={routeId}
              onValueChange={setRouteId}
              options={[
                { value: '', label: 'Choose the route…' },
                ...choices.map((c) => ({ value: c.id, label: c.accountName ? `${c.name} — ${c.accountName}` : c.name })),
              ]}
              aria-label="Route to assign"
            />
          </div>
          <Button onClick={() => setConfirming(true)} disabled={!chosen || assignPending || !relayConfigured}>
            {assignPending && <Spinner className="mr-1 h-4 w-4" />}
            Assign and send
          </Button>
          {!relayConfigured && <span className="text-muted-foreground">The relay is not configured on this environment.</span>}
        </div>
      ) : (
        chosen && (
          <div className="space-y-2" role="alertdialog" aria-label="Confirm assign and send">
            {view.providerAssertion && asserted !== '' && (
              <p className="font-medium">You are asserting this lead is for {systemName(asserted)}. That is recorded on the lead.</p>
            )}
            {confirmLines(chosen.name).map((line) => (
              <p key={line}>{line}</p>
            ))}
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => {
                  setConfirming(false);
                  onAssign(chosen.id, chosen.name, view.providerAssertion && asserted !== '' ? asserted : null);
                }}
              >
                Yes, assign and send
              </Button>
              <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )
      )}
    </section>
  );
}

/** The raw body, indented when it is JSON (it nearly always is); verbatim otherwise. */
function prettyBody(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

type PendingAction = 'resend' | 'keyed';

/**
 * Edit a FAILED lead's own answers. ⚠️ Bound to the lead's INPUT, through the
 * literal EDITABLE_LEAD_FIELDS list — NOT to rekeyFields, whose rows are partly
 * composed values (model-mapped interest, the route's message). Saving sends
 * nothing: Resend is a separate action below. There is deliberately no
 * destination field. The postcode hint cannot block a save — one authority
 * (missingRequiredFields) decides what the provider accepts.
 */
function EditAnswers({
  submission,
  onSave,
}: {
  submission: LeadSubmissionDetail['submission'];
  onSave: (fields: Partial<Record<EditableLeadField, string>>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editable = editableFieldsFor(submission.provider);
  const [values, setValues] = useState<Partial<Record<EditableLeadField, string>>>({});
  const start = () => {
    setValues(Object.fromEntries(editable.map((f) => [f, submission.input[f] ?? ''])));
    setError(null);
    setOpen(true);
  };
  if (!open) {
    return (
      <Button variant="outline" onClick={start} data-testid="edit-answers">
        Edit the lead's answers
      </Button>
    );
  }
  const changed = Object.fromEntries(
    editable.filter((f) => (values[f] ?? '').trim() !== (submission.input[f] ?? '').trim()).map((f) => [f, values[f] ?? '']),
  ) as Partial<Record<EditableLeadField, string>>;
  return (
    <div className="space-y-3 rounded-md border p-3 text-sm" data-testid="edit-answers-form">
      <p className="text-muted-foreground">
        Changes the lead's own answers only — not where it goes. Nothing is sent when you save; Resend is a separate step.
        A value you type where the lead gave none is recorded as entered by you.
      </p>
      <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
        {editable.map((f) => (
          <div key={f} className="contents">
            <label htmlFor={`edit-${f}`} className="text-muted-foreground">
              {EDITABLE_FIELD_LABELS[f]}
            </label>
            <div>
              <input
                id={`edit-${f}`}
                className="w-full rounded-md border bg-white px-2 py-1"
                value={values[f] ?? ''}
                onChange={(e) => setValues((v) => ({ ...v, [f]: e.target.value }))}
              />
              {f === 'postcode' && submission.provider === 'virtualyard' && (
                <p className="mt-0.5 text-xs text-muted-foreground">VirtualYard wants 4 digits (checked when you resend).</p>
              )}
            </div>
          </div>
        ))}
      </div>
      {error && <p className="text-red-700">{error}</p>}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={saving || Object.keys(changed).length === 0}
          onClick={() => {
            setSaving(true);
            setError(null);
            onSave(changed).then(
              () => {
                setSaving(false);
                setOpen(false);
              },
              (e: Error) => {
                setSaving(false);
                setError(e.message);
              },
            );
          }}
        >
          {saving && <Spinner className="mr-1 h-4 w-4" />}
          Save changes
        </Button>
        <Button size="sm" variant="outline" disabled={saving} onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

export function LeadSubmissionPanel({
  detail,
  onResend,
  resendPending,
  onMarkKeyed,
  markKeyedPending,
  onEditAnswers,
  onAssign,
  assignPending = false,
  assignResult = null,
  relayConfigured,
  isAdmin,
}: {
  detail: LeadSubmissionDetail;
  onResend: () => void;
  resendPending: boolean;
  onMarkKeyed: (note: string) => void;
  markKeyedPending: boolean;
  /** Absent = no edit control. Any staff may edit a failed lead (the server refuses any other status). */
  onEditAnswers?: (fields: Partial<Record<EditableLeadField, string>>) => Promise<void>;
  onAssign?: (routeId: string, routeName: string, provider: LeadProvider | null) => void;
  assignPending?: boolean;
  assignResult?: { routeName: string; outcome: RelayOutcome | null; error: string | null } | null;
  relayConfigured: boolean;
  /** ⚠️ Marking an UNASSIGNED lead keyed in by hand is admin-only (keyedManuallyNeedsAdmin). */
  isAdmin: boolean;
}) {
  const { submission, route } = detail;
  const mayKey = mayMarkKeyedManually(submission.status, isAdmin);
  const routingLine = describeRouting(submission);
  // ⚠️ Both actions confirm FIRST, naming the dealer and the customer (§20).
  // Resend stays open to any staff — gating it would slow the one recovery path
  // the design exists for — so the confirmation is where the duplicate risk is
  // stated, rather than an approval step.
  const [confirming, setConfirming] = useState<PendingAction | null>(null);
  // ⚠️ REQUIRED for an unassigned lead (no route records where it went); optional on a failed one.
  const [keyedNote, setKeyedNote] = useState('');
  const noteRequired = keyedManuallyNoteRequired(submission.status);
  const noteMissing = confirming === 'keyed' && noteRequired && keyedNote.trim() === '';
  const confirmLines =
    confirming === 'resend'
      ? resendConfirmation(submission, route?.name ?? null)
      : confirming === 'keyed'
        ? keyedManuallyConfirmation(submission, route?.name ?? null)
        : [];
  const fields = rekeyFields(submission);
  const system = systemName(submission.provider);
  const message = fields.find((f) => f.label === 'Message');
  const short = fields.filter((f) => f.label !== 'Message');
  const missing = new Set(submission.missingFields ?? []);
  const inFlightOnPausedRoute = isInFlight(submission.status) && route?.testMode === true;

  return (
    <div className="space-y-5" data-testid="lead-submission-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={statusBadgeVariant(submission.status)}>{STATUS_LABELS[submission.status]}</Badge>
        {submission.kind === 'test' && <Badge variant="outline">{submission.testOrigin ? 'Test (direct, not sent to CRM)' : 'Test lead'}</Badge>}
        <span className="text-sm text-muted-foreground">
          Received {formatExactTimestamp(submission.receivedAt)}
          {submission.resolvedStore ? ` · ${submission.resolvedStore.name}` : ''}
          {submission.kind === 'live' ? ` · Platform: ${platformLabel(submission.input.platform)}` : ''}
        </span>
      </div>

      {assignResult && (
        <AssignSendResult
          routeName={assignResult.routeName}
          outcome={assignResult.outcome}
          error={assignResult.error}
          provider={submission.provider}
        />
      )}

      {canAssign(submission.status) && detail.assignment && (
        <UnassignedSection
          view={detail.assignment}
          onAssign={onAssign}
          assignPending={assignPending}
          relayConfigured={relayConfigured}
          confirmLines={(routeName) => assignConfirmation(submission, routeName)}
        />
      )}

      {routingLine && (
        <p className="text-sm text-muted-foreground" data-testid="routing-note">
          {routingLine}
        </p>
      )}

      {submission.status === 'failed' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="font-medium">
            This lead did not reach {system}
            {submission.failureReason ? ` — ${failureLabel(submission.failureReason, submission.provider)}` : ''}.
          </p>
          {submission.failureReason === 'provider_unrecognised_response' &&
            hitUnrecognisedCeiling(submission.attempts.slice(submission.attemptSequenceStart)) && (
              <p className="mt-1 font-medium" data-testid="retries-stopped">
                {unrecognisedCeilingMessage(system)}
              </p>
            )}
          <p className="mt-1">
            Key it into {system} by hand using the details below and then mark it as keyed, or Resend it once the
            cause is fixed.
          </p>
        </div>
      )}

      {submission.status === 'keyed_manually' && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="keyed-manually-note">
          Keyed in by hand
          {submission.keyedManuallyBy ? ` by ${submission.keyedManuallyBy}` : ''}
          {submission.keyedManuallyAt ? ` on ${formatExactTimestamp(submission.keyedManuallyAt)}` : ''}.{' '}
          {submission.routeId === null
            ? 'It had no route: it was never sent and can no longer be assigned.'
            : 'It no longer counts as a failure and cannot be resent.'}
          {submission.failureReason ? ` It originally failed: ${failureLabel(submission.failureReason, submission.provider)}.` : ''}
          {submission.keyedManuallyNote && (
            <p className="mt-1 whitespace-pre-wrap" data-testid="keyed-manually-note-text">
              Note: {submission.keyedManuallyNote}
            </p>
          )}
        </div>
      )}

      {submission.testDifferences && submission.testDifferences.length > 0 && (
        // ⚠️ What the third party received differed from production. Shown,
        // because a gap the operator cannot see is how a passing test stops
        // meaning anything.
        <div
          className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="test-differences"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <p className="font-medium">This test lead was sent differently from a live one:</p>
            <ul className="mt-1 space-y-1">
              {submission.testDifferences.map((d) => (
                <li key={d.field}>
                  <span className="font-mono">{d.field}</span> was {d.test} (a live lead sends {d.production}) — {d.why}.
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {nameNotes(submission).length > 0 && (
        <div
          className="flex gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
          data-testid="name-notes"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <ul className="space-y-1">
            {nameNotes(submission).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {editNotes(submission).length > 0 && (
        // Same place and voice as the name-split note: a value a PERSON supplied
        // looks exactly like the customer's own answer once it is in the dealer's CRM.
        <div
          className="flex gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
          data-testid="edit-notes"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <ul className="space-y-1">
              {editNotes(submission).map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            {hasUnsentEdit(submission) && (
              <p className="mt-1 font-medium" data-testid="edit-unsent">
                Resend to apply this — nothing has been sent since the change.
              </p>
            )}
          </div>
        </div>
      )}

      {payloadNotes(submission).length > 0 && (
        <div
          className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          data-testid="payload-notes"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <ul className="space-y-1">
            {payloadNotes(submission).map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {inFlightOnPausedRoute && (
        <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>{testModeInFlightNote(submission.provider)}</p>
        </div>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold">Customer details</h3>
        <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[10rem_1fr]">
          {short.map((field) => {
            const gap = field.required && field.value.trim() === '';
            return (
              <div key={field.label} className="contents">
                <dt className="text-sm text-muted-foreground">
                  {field.label}
                  {field.required && <span className="sr-only"> (required)</span>}
                </dt>
                <dd className={gap ? 'text-sm font-medium text-red-700' : 'text-sm'}>
                  {gap && submission.provider ? (
                    requiredGapText(submission.provider)
                  ) : field.value.trim() === '' && field.placeholder ? (
                    <span className="text-muted-foreground">{field.placeholder}</span>
                  ) : (
                    <CopyableCell value={field.value} ariaLabel={`Copy ${field.label}`} />
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
        {missing.size > 0 && (
          <p className="mt-2 text-sm text-red-700">
            Missing or invalid: {[...missing].join(', ')}.
          </p>
        )}
        {submission.interestFallback && submission.interestFallback !== 'none' && (
          <p className="mt-2 text-sm text-muted-foreground">
            {INTEREST_FALLBACK_LABELS[submission.interestFallback]}
            {submission.input.model ? ` (they answered "${submission.input.model}").` : '.'}
          </p>
        )}
      </section>

      {message && (
        <section>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-sm font-semibold">Message</h3>
            {message?.value && <CopyableCell value={message.value} display="Copy message" ariaLabel="Copy message" />}
          </div>
          {message?.value ? (
            <pre className="whitespace-pre-wrap rounded-md border bg-muted/40 p-3 font-sans text-sm">{message.value}</pre>
          ) : (
            <p className="text-sm text-red-700">No message was composed for this lead.</p>
          )}
        </section>
      )}

      {onEditAnswers && canEditLead(submission.status) && confirming === null && (
        <EditAnswers key={submission.inputEdits?.length ?? 0} submission={submission} onSave={onEditAnswers} />
      )}

      {(canResend(submission.status) || mayKey) && confirming === null && (
        <div className="flex flex-wrap items-center gap-3">
          {mayKey && (
            <Button variant="outline" onClick={() => setConfirming('keyed')} disabled={markKeyedPending}>
              {markKeyedPending && <Spinner className="mr-1 h-4 w-4" />}
              Mark keyed in by hand
            </Button>
          )}
          {canResend(submission.status) && (
            <Button onClick={() => setConfirming('resend')} disabled={resendPending || !relayConfigured}>
              {resendPending && <Spinner className="mr-1 h-4 w-4" />}
              Resend through the relay
            </Button>
          )}
          {canResend(submission.status) && !relayConfigured && (
            <span className="text-sm text-muted-foreground">The relay is not configured on this environment.</span>
          )}
        </div>
      )}

      {confirming !== null && (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alertdialog" aria-label="Confirm">
          {confirmLines.map((line) => (
            <p key={line}>{line}</p>
          ))}
          {confirming === 'keyed' && (
            <div>
              <label htmlFor="keyed-note" className="font-medium">
                Note{noteRequired ? ' (required)' : ' (optional)'}
              </label>
              <Textarea
                id="keyed-note"
                className="mt-1 bg-white"
                value={keyedNote}
                maxLength={KEYED_MANUALLY_NOTE_MAX}
                placeholder={noteRequired ? 'Where you keyed it in, and why it had no route' : 'Anything worth recording'}
                onChange={(e) => setKeyedNote(e.target.value)}
                data-testid="keyed-note-input"
              />
            </div>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={noteMissing}
              onClick={() => {
                const action = confirming;
                setConfirming(null);
                if (action === 'resend') onResend();
                else onMarkKeyed(keyedNote.trim());
              }}
            >
              {confirming === 'resend' ? 'Yes, resend it' : 'Yes, I keyed it in'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirming(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {submission.rawBody !== undefined && (
        // The body exactly as it arrived — open by default when the lead is
        // unassigned, because then it is the thing a person works from.
        <details open={submission.status === 'unassigned'} data-testid="raw-body">
          <summary className="cursor-pointer text-sm font-semibold">Raw body as received</summary>
          <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/40 p-3 text-xs">
            {prettyBody(submission.rawBody)}
          </pre>
        </details>
      )}

      <section>
        <h3 className="mb-2 text-sm font-semibold">Delivery attempts ({submission.attempts.length})</h3>
        {submission.providerRef && (
          // ⚠️ externalId is the PERSON AT THE CLUB (it repeats across their
          // enquiries); leadId is THIS submission's own reference (Addendum 1).
          <p className="mb-2 text-sm" data-testid="provider-ref">
            {systemName(submission.provider)} reference: <span className="font-mono">{submission.providerRef.refId}</span>{' '}
            <span className="text-muted-foreground">
              {submission.providerRef.refIdSource === 'external_id'
                ? '(the person at this club — it repeats across their enquiries)'
                : `(${submission.providerRef.refIdSource.replace(/_/g, ' ')})`}
            </span>
            {submission.providerRef.leadId && (
              <>
                {' '}· this enquiry&apos;s lead id <span className="font-mono">{submission.providerRef.leadId}</span>
              </>
            )}
          </p>
        )}
        {submission.attempts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No attempt has been made yet.</p>
        ) : (
          <ol className="space-y-2">
            {submission.attempts.map((attempt, i) => (
              <li key={`${attempt.at}-${i}`} className="rounded-md border p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">#{i + 1}</span>
                  <span>{formatExactTimestamp(attempt.at)}</span>
                  <Badge variant={attempt.outcome === 'ok' ? 'success' : attempt.outcome === 'unknown' ? 'warning' : 'destructive'}>
                    {attempt.outcome === 'ok' ? 'Accepted' : attempt.outcome === 'unknown' ? 'May have been received' : 'Failed'}
                  </Badge>
                  {attempt.httpStatus !== null && <span className="text-muted-foreground">HTTP {attempt.httpStatus}</span>}
                  {i === submission.attemptSequenceStart && i > 0 && (
                    <span className="text-muted-foreground">— resent{submission.resentBy ? ` by ${submission.resentBy}` : ''}</span>
                  )}
                </div>
                {attempt.error && <p className="mt-1 text-muted-foreground">{attempt.error}</p>}
                {attempt.responseBody && (
                  <details className="mt-1">
                    <summary className="cursor-pointer text-muted-foreground">{system}'s response</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all text-xs">{attempt.responseBody}</pre>
                  </details>
                )}
              </li>
            ))}
          </ol>
        )}
        {submission.status === 'retrying' && submission.nextAttemptAt && (
          <p className="mt-2 text-sm text-muted-foreground">Next attempt {formatExactTimestamp(submission.nextAttemptAt)}.</p>
        )}
      </section>
    </div>
  );
}
